import React, {useEffect, useRef, useState} from "react";
import {Redirect, useParams} from "react-router-dom";
import {Button, Divider, Grid, Toolbar} from "@material-ui/core";
import Container from "@material-ui/core/Container";
import Paper from "@material-ui/core/Paper";
import Tooltip from "@material-ui/core/Tooltip";
import {makeStyles} from "@material-ui/core/styles";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import {PDFExport} from "@progress/kendo-react-pdf";
import {NotificationManager} from "react-notifications";
import UserService from "../../services/user.service";
import {logOut} from "../../helpers/logout";
import {getPdfExportProxyProps, savePdfWithEmbeddedImages} from "../../helpers/pdfExport";
import ActionButton from "../form/input/ActionButton";
import GeneratedDocumentEmailAction from "./GeneratedDocumentEmailAction";
import udzsLogo from "../../assets/documents/udzs-logo.png";
import "../../Print.css";

const useStyles = makeStyles((theme) => ({
    root: {flexGrow: 1, width: "100%", backgroundColor: theme.palette.background.paper},
    seeMore: {marginTop: theme.spacing(8)},
    paper: {padding: theme.spacing(2), display: "flex", overflow: "auto", flexDirection: "column"},
    marginTop: {marginTop: theme.spacing(2)},
    editHint: {margin: "0 0 12px", fontWeight: 700, textAlign: "center", color: "#2c4767"},
    pages: {background: "transparent"},
    page: {
        boxSizing: "border-box", display: "flex", flexDirection: "column", width: "210mm", height: "297mm", padding: "12mm 15mm 9mm", overflow: "hidden",
        background: "#fff", color: "#111", fontFamily: 'Arial, "DejaVu Sans", sans-serif', fontSize: 11.5, lineHeight: 1.35,
    },
    secondPage: {pageBreakBefore: "always"},
    top: {display: "grid", gridTemplateColumns: "48mm 1fr 42mm", alignItems: "start", minHeight: "35mm"},
    logo: {width: "40mm", maxHeight: "18mm", objectFit: "contain"},
    appendix: {textAlign: "right", fontSize: 10.5},
    title: {margin: "11mm 0 1mm", textAlign: "center", fontSize: 17, lineHeight: 1.2},
    subtitle: {margin: 0, textAlign: "center", fontSize: 13, fontStyle: "italic", fontWeight: 400},
    form: {marginTop: "9mm"},
    lineRow: {display: "flex", alignItems: "baseline", gap: 4, minHeight: "11mm"},
    lineLabel: {whiteSpace: "nowrap", fontWeight: 700},
    input: {minWidth: 0, flex: 1, height: "5mm", padding: "0 1mm", border: 0, borderBottom: "1px dotted #222", background: "transparent", color: "#111", fontFamily: 'Arial, "DejaVu Sans", sans-serif', fontSize: 11.5},
    fixedSmall: {flex: "0 0 34mm"},
    hint: {margin: "-3mm 0 2mm 101mm", fontSize: 8.5, fontStyle: "italic"},
    choiceRow: {display: "flex", alignItems: "center", minHeight: "10mm"},
    choiceLabel: {width: "74mm", fontWeight: 700},
    choice: {display: "inline-flex", alignItems: "center", gap: "2mm", marginRight: "16mm", fontWeight: 700, cursor: "pointer"},
    checkbox: {display: "inline-flex", alignItems: "center", justifyContent: "center", width: "7mm", height: "7mm", border: "1px solid #111", background: "#fff", fontSize: 15, lineHeight: 1},
    signatureRow: {marginTop: "4mm"},
    signatureInput: {display: "block", width: "72mm", margin: "4mm 13mm 0 auto"},
    separator: {margin: "4mm 0 7mm", border: 0, borderTop: "1px dashed #111"},
    sectionTitle: {margin: "0 0 1mm", textAlign: "center", fontSize: 17},
    sectionSubtitle: {margin: "0 0 7mm", textAlign: "center", fontSize: 12, fontStyle: "italic", fontWeight: 400},
    subsection: {margin: "0 0 6mm", fontSize: 12.5},
    transportForm: {marginTop: "1mm"},
    notice: {marginTop: "3mm", fontSize: 9.5, fontStyle: "italic"},
    pageNumber: {marginTop: "auto", textAlign: "center", fontWeight: 700},
    secondTitle: {marginTop: 0},
    secondBlock: {marginTop: "8mm"},
    divider: {margin: "7mm 0 10mm", border: 0, borderTop: "1px solid #111"},
    choicesGrid: {display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6mm 8mm", marginTop: "7mm"},
    choiceGroupTitle: {minHeight: "9mm", fontWeight: 700},
    compactChoice: {display: "flex", alignItems: "center", gap: "5mm"},
    compactOption: {display: "inline-flex", alignItems: "center", gap: "2mm", fontWeight: 700, cursor: "pointer"},
    footnote: {marginTop: "auto", fontSize: 9.5, fontStyle: "italic"},
}));

const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("sk-SK", {day: "2-digit", month: "2-digit", year: "numeric"});
};

const formatDateTime = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.toLocaleDateString("sk-SK", {day: "2-digit", month: "2-digit", year: "numeric"})} ${date.toLocaleTimeString("sk-SK", {hour: "2-digit", minute: "2-digit"})}`;
};

const yesNoValue = (value) => {
    if (!value) return "";
    const normalizedValue = value.trim().toLocaleUpperCase("sk-SK");
    if (normalizedValue.startsWith("ÁNO")) return "yes";
    if (normalizedValue.startsWith("NIE")) return "no";
    return "";
};

const initialFields = {
    deceased: "", deceasedIdBirth: "", birthPlace: "", deathDateTime: "", deathPlace: "", examinationDetails: "",
    dangerousDisease: "", doctorProposal: "", transportPermit: "", doctorSignature: "",
    companyName: "", contractNumber: "", companyAddress: "", companyIco: "", driver: "", transportFrom: "", transportTo: "", transportDate: "", kilometers: "", driverSignature: "",
    slapa: "", receivedBy: "", receivedDate: "", receivedTime: "", receivedSignature: "", bodiesCount: "",
    autopsyPerformed: "", autopsyDangerousDisease: "", criminalProcedure: "", burialTransportPermit: "", autopsyPlace: "", autopsyDateTime: "", autopsyDoctorSignature: "", slapaStamp: "",
};

const ConfirmationContext = React.createContext(null);

const LineInput = ({name, className = ""}) => {
    const {classes, fields, handleChange} = React.useContext(ConfirmationContext);
    return <input name={name} value={fields[name]} onChange={handleChange} className={`${classes.input} ${className}`}/>;
};

const LineRow = ({label, name, children}) => {
    const {classes} = React.useContext(ConfirmationContext);
    return <label className={classes.lineRow}><span className={classes.lineLabel}>{label}</span>{children || <LineInput name={name}/>}</label>;
};

const Choice = ({group, value, label, compact = false}) => {
    const {classes, fields, handleChoice} = React.useContext(ConfirmationContext);
    return (
        <span className={compact ? classes.compactOption : classes.choice} onClick={() => handleChoice(group, value)}>
            <span>{label}</span><span className={classes.checkbox}>{fields[group] === value ? "✓" : ""}</span>
        </span>
    );
};

const BinaryChoice = ({group}) => {
    const {classes} = React.useContext(ConfirmationContext);
    return <div className={classes.compactChoice}><Choice compact group={group} value="yes" label="Áno"/><Choice compact group={group} value="no" label="Nie"/></div>;
};

const DeathExaminationConfirmationGenerator = (props) => {
    const classes = useStyles();
    const pdfRef = useRef(null);
    const funeralId = useParams();
    const userLocal = JSON.parse(localStorage.getItem("user")).id;
    const [funeral, setFuneral] = useState({_id: "", deceased: "", ca_email: "", evidence: {}});
    const [user, setUser] = useState(null);
    const [fields, setFields] = useState(initialFields);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const handleLoadError = (error) => {
            if (error.response?.status === 401) {
                logOut(props);
                NotificationManager.warning("Potrebné sa znova prihlásiť!", "Upozornenie", 5000);
            }
        };

        UserService.getUser(userLocal).then(({data}) => {
            setUser(data);
            setFields((current) => ({...current,
                companyName: data.name || "",
                companyAddress: data.address || "",
                companyIco: data.ico || "",
            }));
        }, handleLoadError);

        UserService.getFuneral(funeralId.funeral_id).then(({data}) => {
            const evidence = data.evidence || {};
            const autopsy = yesNoValue(evidence.autopsy);
            let doctorProposal = "";
            if (autopsy === "yes") doctorProposal = "autopsy";
            if (autopsy === "no") doctorProposal = "burial";
            setFuneral(data);
            setFields((current) => ({...current,
                deceased: data.deceased || "",
                deceasedIdBirth: data.d_id || formatDate(data.d_birth),
                birthPlace: data.d_place_birth || "",
                deathDateTime: formatDateTime(data.d_death),
                deathPlace: data.d_place_death || "",
                dangerousDisease: yesNoValue(evidence.d_dangerous_disease),
                doctorProposal,
                driver: evidence.employee_transfer || "",
                transportFrom: data.planned_pickup_place || data.d_place_death || "",
                transportTo: evidence.placement_of_decedent || "",
                transportDate: formatDate(data.planned_pickup),
            }));
        }, handleLoadError);
    }, [funeralId.funeral_id, userLocal, props]);

    const handleChange = ({target: {name, value}}) => setFields((current) => ({...current, [name]: value}));
    const handleChoice = (group, value) => setFields((current) => ({...current, [group]: current[group] === value ? "" : value}));
    const handleExport = () => savePdfWithEmbeddedImages(pdfRef, (error) => {
        if (error) NotificationManager.error("Nepodarilo sa exportovať PDF!", "Chyba", 5000);
    });
    const fileSuffix = (funeral.deceased || "zosnuly").replace(/\s/g, "");

    return (
        <Container maxWidth="lg" className={classes.seeMore}>
            <div className={classes.root}><Paper className={classes.paper}>
                <Grid container>
                    <Grid xs={12} sm={4} md={3} item container><Toolbar><Button variant="contained" color="secondary" onClick={() => setSuccess(true)}>Späť na detaily</Button></Toolbar></Grid>
                    <Grid xs={12} sm={8} md={9} item container justifyContent="flex-end"><Toolbar className="document-actions-toolbar">
                        <ActionButton color="primary"><Tooltip title="Pdf export"><PictureAsPdfIcon fontSize="small" onClick={handleExport}/></Tooltip></ActionButton>
                        <GeneratedDocumentEmailAction pdfRef={pdfRef} funeral={funeral} user={user}/>
                    </Toolbar></Grid>
                </Grid>
                <Divider/>
                <Grid className={classes.marginTop} container direction="column" alignItems="center">
                    <div className={classes.editHint}>Po KLIKNUTÍ viete dopisovať alebo meniť údaje.</div>
                    <div className="document-preview-frame">
                        <PDFExport ref={pdfRef} {...getPdfExportProxyProps()} paperSize="A4" scale={0.75} forcePageBreak=".death-confirmation-page-break" fileName={`potvrdenie-o-prehliadke-mrtveho-tela-${fileSuffix}`}>
                            <ConfirmationContext.Provider value={{classes, fields, handleChange, handleChoice}}>
                                <div className={classes.pages}>
                                    <div className={classes.page}>
                                        <div className={classes.top}>
                                            <img src={udzsLogo} className={classes.logo} alt="Úrad pre dohľad nad zdravotnou starostlivosťou"/>
                                            <div><h1 className={classes.title}>Potvrdenie o prehliadke mŕtveho tela</h1><h2 className={classes.subtitle}>(vypĺňa prehliadajúci lekár pri každej prehliadke)</h2></div>
                                            <div className={classes.appendix}>Príloha č. 1 - dynamická</div>
                                        </div>

                                        <div className={classes.form}>
                                            <LineRow label="Meno a priezvisko zosnulého:" name="deceased"/>
                                            <div className={classes.lineRow}><span className={classes.lineLabel}>RČ/Dátum nar.:</span><LineInput name="deceasedIdBirth"/><span className={classes.lineLabel}>Miesto narodenia:</span><LineInput name="birthPlace"/></div>
                                            <div className={classes.lineRow}><span className={classes.lineLabel}>Dátum a čas úmrtia:</span><LineInput name="deathDateTime"/><span className={classes.lineLabel}>Miesto úmrtia:</span><LineInput name="deathPlace"/></div>
                                            <div className={classes.hint}>(doma, ulica, nemocnica, ZpDCH, pri preprave, iné ...)</div>
                                            <LineRow label="Miesto, dátum a čas prehliadky:" name="examinationDetails"/>
                                            <div className={classes.choiceRow}><span className={classes.choiceLabel}>Úmrtie na nebezpečnú chorobu:</span><Choice group="dangerousDisease" value="no" label="Nie"/><Choice group="dangerousDisease" value="yes" label="Áno"/></div>
                                            <div className={classes.choiceRow}><span className={classes.choiceLabel}>Návrh prehliadajúceho lekára:</span><Choice group="doctorProposal" value="burial" label="Pochovať"/><Choice group="doctorProposal" value="autopsy" label="Výkon pitvy"/></div>
                                            <div className={classes.choiceRow}><span className={classes.choiceLabel}>Povoľuje sa preprava na:</span><Choice group="transportPermit" value="burial" label="Pochovanie"/><Choice group="transportPermit" value="autopsy" label="Pitvu"/></div>
                                            <div className={classes.signatureRow}><strong>Pečiatka a podpis prehliadajúceho lekára:</strong><LineInput name="doctorSignature" className={classes.signatureInput}/></div>
                                        </div>

                                        <hr className={classes.separator}/>
                                        <h2 className={classes.sectionTitle}>Potvrdenie o preprave tela na SLaPA pracovisko na pitvu</h2>
                                        <h3 className={classes.sectionSubtitle}>(vypĺňa zmluvná pohrebná služba v prípade prepravy na pitvu)</h3>
                                        <h4 className={classes.subsection}>Identifikácia prevádzkovateľa pohrebnej služby</h4>
                                        <div className={classes.transportForm}>
                                            <LineRow label="Názov:" name="companyName"/>
                                            <LineRow label="Číslo zmluvy uzatvorenej s ÚDZS:" name="contractNumber"/>
                                            <LineRow label="Adresa:" name="companyAddress"/>
                                            <LineRow label="IČO:"><LineInput name="companyIco" className={classes.fixedSmall}/></LineRow>
                                            <LineRow label="Meno a priezvisko vodiča:"><LineInput name="driver" className={classes.fixedSmall}/></LineRow>
                                            <LineRow label="Preprava: odkiaľ:" name="transportFrom"/>
                                            <LineRow label="kam:" name="transportTo"/>
                                            <div className={classes.lineRow}><span className={classes.lineLabel}>Dňa:</span><LineInput name="transportDate"/><span className={classes.lineLabel}>počet km:</span><LineInput name="kilometers"/><span className={classes.lineLabel}>podpis vodiča:</span><LineInput name="driverSignature"/></div>
                                        </div>
                                        <div className={classes.notice}>Poučenie: tlačivo slúži pre potreby manipulácie s mŕtvym telom na základe ustanovení zákona č. 131/2010 Z. z.</div>
                                        <div className={classes.pageNumber}>Strana 1 z 2</div>
                                    </div>

                                    <div className={`${classes.page} ${classes.secondPage} death-confirmation-page-break`}>
                                        <h2 className={`${classes.sectionTitle} ${classes.secondTitle}`}>Potvrdenie o prevzatí tela na pracovisko</h2>
                                        <h3 className={classes.sectionSubtitle}>(vypĺňa pracovisko SLaPA)</h3>
                                        <div className={classes.secondBlock}>
                                            <LineRow label="SLaPA:" name="slapa"/>
                                            <LineRow label="Prevzal:" name="receivedBy"/>
                                            <div className={classes.lineRow}><span className={classes.lineLabel}>Dátum:</span><LineInput name="receivedDate"/><span className={classes.lineLabel}>čas:</span><LineInput name="receivedTime"/><span>hod.,</span><span className={classes.lineLabel}>podpis:</span><LineInput name="receivedSignature"/></div>
                                            <LineRow label="Počet mŕtvych tiel prepravených na pitvu v jednom vozidle:" name="bodiesCount"/>
                                        </div>
                                        <hr className={classes.divider}/>
                                        <h2 className={classes.sectionTitle}>Potvrdenie o vykonaní pitvy</h2>
                                        <h3 className={classes.sectionSubtitle}>(vypĺňa lekár SLaPA pracoviska)</h3>
                                        <div className={classes.choicesGrid}>
                                            <div><div className={classes.choiceGroupTitle}>Pitva vykonaná:</div><BinaryChoice group="autopsyPerformed"/></div>
                                            <div><div className={classes.choiceGroupTitle}>Úmrtie na nebezpečnú chorobu:</div><BinaryChoice group="autopsyDangerousDisease"/></div>
                                            <div><div className={classes.choiceGroupTitle}>Pitva podľa Trestného poriadku*:</div><BinaryChoice group="criminalProcedure"/></div>
                                            <div><div className={classes.choiceGroupTitle}>Povoľuje sa preprava na pochovanie:</div><BinaryChoice group="burialTransportPermit"/></div>
                                        </div>
                                        <div className={classes.secondBlock}>
                                            <LineRow label="Miesto výkonu pitvy:" name="autopsyPlace"/>
                                            <LineRow label="Dátum a čas pitvy:" name="autopsyDateTime"/>
                                        </div>
                                        <div className={classes.secondBlock}>
                                            <LineRow label="Pečiatka a podpis lekára, ktorý vykonal pitvu:" name="autopsyDoctorSignature"/>
                                            <LineRow label="Pečiatka SLaPA pracoviska:" name="slapaStamp"/>
                                        </div>
                                        <div className={classes.footnote}>* V prípade pitvy nariadenej podľa Trestného poriadku je na pochovanie tela potrebný súhlas prokurátora.<br/>Poučenie: tlačivo slúži pre potreby manipulácie s mŕtvym telom na základe ustanovení zákona č. 131/2010 Z. z.<br/>Pri preprave na pitvu objednáva zmluvnú pohrebnú službu pre daný región prehliadajúci lekár zaradený do rozpisu úradu, ktorý vykonal prehliadku.</div>
                                        <div className={classes.pageNumber}>Strana 2 z 2</div>
                                    </div>
                                </div>
                            </ConfirmationContext.Provider>
                        </PDFExport>
                    </div>
                </Grid>
            </Paper></div>
            {success ? <Redirect to={{pathname: `/pohreb/${funeralId.funeral_id}/info`, state: {from: props.location, number: 5}}}/> : <></>}
        </Container>
    );
};

export default DeathExaminationConfirmationGenerator;
